"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import type { NavLink } from "@/lib/nav-links";

export function MobileNav({ links }: { links: NavLink[] }) {
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const firstLinkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (!open) return;
    firstLinkRef.current?.focus();
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  function close() {
    setOpen(false);
    toggleRef.current?.focus();
  }

  return (
    <div className="md:hidden">
      <button
        type="button"
        ref={toggleRef}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="mobile-nav-panel"
        aria-label={open ? "메뉴 닫기" : "메뉴 열기"}
        className="flex h-9 w-9 items-center justify-center rounded-md text-lg text-ink hover:bg-surface-soft"
      >
        {open ? "✕" : "☰"}
      </button>

      {open &&
        createPortal(
          <>
            <div className="fixed inset-0 top-16 z-50 bg-ink/40" onMouseDown={close} />
            <div
              id="mobile-nav-panel"
              role="dialog"
              aria-modal="true"
              aria-label="모바일 메뉴"
              className="fixed inset-x-0 top-16 z-50 max-h-[calc(100vh-4rem)] overflow-y-auto border-b border-hairline bg-canvas shadow-lg"
            >
              <nav className="flex flex-col">
                {links.map((l, i) => (
                  <Link
                    key={l.href}
                    href={l.href}
                    ref={i === 0 ? firstLinkRef : undefined}
                    onClick={close}
                    className="border-b border-hairline-soft px-6 py-3.5 text-base font-medium text-ink last:border-b-0 hover:bg-surface-soft"
                  >
                    {l.label}
                  </Link>
                ))}
              </nav>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
